import type { ReactNode } from 'react';
import { Rocket, Zap, Flame, Turtle, InfinityIcon } from 'lucide-react';

/**
 * Glifi Lucide per categoria di cadenza (bullet/blitz/rapid/classical/none).
 *
 * Sorgente UNICA: chi mostra una cadenza passa da `cadenceIcon()` o da
 * `CADENCE_ICONS`, mai da una copia locale. Le copie sono gia' esistite (profilo,
 * classifica, statistiche) e sono andate in deriva appena qui e' cambiato il fill.
 *
 * Tutte a contorno, senza `fill`: un gruppo di cadenze si legge come un unico peso
 * ottico solo se nessuna e' piena, e razzo/tartaruga riempiti a 12-14px diventano
 * macchie illeggibili. Non aggiungere `fill` a una sola voce.
 */
const CADENCE_GLYPHS = {
  bullet: Rocket,
  blitz: Zap,
  rapid: Flame,
  classical: Turtle,
  none: InfinityIcon,
} as const;

/**
 * Icona della cadenza alla dimensione che serve al chiamante: 12 per i micro-chip,
 * 14 per chip e pastiglie (il default), 18 per i riquadri rating del profilo.
 * Ritorna `null` per categorie sconosciute, cosi' `cadenceIcon(x) ?? fallback` regge.
 */
export function cadenceIcon(category: string, size = 14): ReactNode {
  const Glyph = CADENCE_GLYPHS[category as keyof typeof CADENCE_GLYPHS];
  return Glyph ? <Glyph size={size} /> : null;
}

/** Il set a 14px, pronto all'uso per i chip. Voci sconosciute -> `undefined`. */
export const CADENCE_ICONS: Record<string, ReactNode> = Object.fromEntries(
  Object.keys(CADENCE_GLYPHS).map((category) => [category, cadenceIcon(category)]),
);

/** "blitz-5+3" → { category: "blitz", clock: "5+3" } */
export function parseTimeControl(tc: string | null): { category: string; clock: string } | null {
  if (!tc) return null;
  const [category, ...rest] = tc.split('-');
  if (!category) return null;
  return { category: category.toLowerCase(), clock: rest.join('-') };
}
